import { useEffect, useRef, useState, useCallback } from 'react';

export interface MenuItem {
  label: string;
  onClick: () => void;
  /** Renders in the danger color and separated from the safe actions. */
  danger?: boolean;
  disabled?: boolean;
  /** Shown as a native tooltip — used to say WHY an item is disabled. */
  title?: string;
}

interface KebabMenuProps {
  items: MenuItem[];
  ariaLabel: string;
}

/**
 * The three-dot overflow menu (Jakob's law: the pattern every table user
 * already knows). Rows keep ONE visible contextual action; everything else
 * lives here, which keeps the row scannable (Hick's law) without hiding
 * capability.
 *
 * Accessibility is the point of having a shared component instead of a div
 * with an onClick: real `menu`/`menuitem` roles, aria-expanded on the trigger,
 * ArrowUp/ArrowDown to move, Home/End to jump, Escape to close with focus
 * returned to the trigger, and click-outside to dismiss.
 */
export function KebabMenu({ items, ariaLabel }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open) {
      // Focus the first enabled item so the keyboard picks up where the click left off.
      const first = itemRefs.current.find((el, i) => el && !items[i]?.disabled);
      first?.focus();
    }
  }, [open, items]);

  const focusItem = (from: number, step: 1 | -1) => {
    const enabled = itemRefs.current
      .map((el, i) => ({ el, i }))
      .filter(({ el, i }) => el && !items[i]?.disabled);
    if (enabled.length === 0) return;
    const pos = enabled.findIndex(({ i }) => i === from);
    const next = enabled[(pos + step + enabled.length) % enabled.length];
    next.el?.focus();
  };

  const safeItems = items.filter((i) => !i.danger);
  const dangerItems = items.filter((i) => i.danger);

  const renderItem = (item: MenuItem, index: number) => (
    <button
      key={item.label}
      ref={(el) => {
        itemRefs.current[index] = el;
      }}
      type="button"
      role="menuitem"
      className={`ui-menuItem${item.danger ? ' ui-menuItem--danger' : ''}`}
      disabled={item.disabled}
      title={item.title}
      onClick={() => {
        close(true);
        item.onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          focusItem(index, 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          focusItem(index, -1);
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          focusItem(e.key === 'Home' ? items.length - 1 : 0, e.key === 'Home' ? 1 : -1);
        }
      }}
    >
      {item.label}
    </button>
  );

  return (
    <div
      className="ui-kebabRoot"
      ref={rootRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          close(true);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="ui-kebabTrigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && (
        <div className="ui-menu" role="menu" aria-label={ariaLabel}>
          {safeItems.map((item) => renderItem(item, items.indexOf(item)))}
          {dangerItems.length > 0 && safeItems.length > 0 && (
            <div className="ui-menuDivider" role="separator" />
          )}
          {dangerItems.map((item) => renderItem(item, items.indexOf(item)))}
        </div>
      )}
    </div>
  );
}
